import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import organizationService from '@/services/organizationService';
import type { Organization } from '@/services/organizationService';
import type {
  ShareOrganizationRequestDto,
  ShareOrganizationResponseDto,
} from '@/types/organization';

interface OrganizationState {
  sharing: boolean;
  shareError: string | null;
  lastShare?: ShareOrganizationResponseDto | null;
  organizations: Organization[];
  loading: boolean;
  error: string | null;
}

const initialState: OrganizationState = {
  sharing: false,
  shareError: null,
  lastShare: null,
  organizations: [],
  loading: false,
  error: null,
};

export const fetchMyOrganizations = createAsyncThunk(
  'organization/fetchMyOrganizations',
  async (_, { rejectWithValue }) => {
    try {
      const response = await organizationService.getMyOrganizations();
      return response;
    } catch (error) {
      const err = error as { response?: { data?: { message?: string } } };
      return rejectWithValue(
        err.response?.data?.message || 'Failed to fetch organizations'
      );
    }
  }
);

export const shareOrganization = createAsyncThunk(
  'organization/share',
  async (
    {
      organizationId,
      dto,
    }: { organizationId: string; dto: ShareOrganizationRequestDto },
    { rejectWithValue }
  ) => {
    try {
      const response = await organizationService.share(organizationId, dto);
      return response;
    } catch (error) {
      const err = error as { response?: { data?: { message?: string } } };
      return rejectWithValue(
        err.response?.data?.message || 'Failed to share organization'
      );
    }
  }
);

const organizationSlice = createSlice({
  name: 'organization',
  initialState,
  reducers: {
    clearShareState: (state) => {
      state.sharing = false;
      state.shareError = null;
      state.lastShare = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch my organizations
      .addCase(fetchMyOrganizations.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchMyOrganizations.fulfilled,
        (state, action: PayloadAction<Organization[]>) => {
          state.loading = false;
          state.organizations = action.payload;
        }
      )
      .addCase(fetchMyOrganizations.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) || 'Unknown error';
      })
      // Share organization
      .addCase(shareOrganization.pending, (state) => {
        state.sharing = true;
        state.shareError = null;
      })
      .addCase(
        shareOrganization.fulfilled,
        (state, action: PayloadAction<ShareOrganizationResponseDto>) => {
          state.sharing = false;
          state.lastShare = action.payload;
        }
      )
      .addCase(shareOrganization.rejected, (state, action) => {
        state.sharing = false;
        state.shareError = (action.payload as string) || 'Unknown error';
      });
  },
});

export const { clearShareState } = organizationSlice.actions;
export default organizationSlice.reducer;
